import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { comics, categories, bannerComics } from "@/mocks/comics";
import { siteInfo } from "@/mocks/site";
import ComicCard from "@/components/feature/ComicCard";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % bannerComics.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const hotComics = comics.filter((c) => c.rating >= 9.5).slice(0, 8);
  const latestComics = [...comics].sort(
    (a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()
  ).slice(0, 8);
  const rankComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 10);

  return (
    <main>
      <Breadcrumb items={[]} />

      {/* Hero Banner */}
      <section className="relative h-[420px] md:h-[600px] overflow-hidden -mt-10">
        {bannerComics.map((banner, idx) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-all duration-700 ${
              idx === currentBanner ? "opacity-100 scale-100" : "opacity-0 scale-105"
            }`}
          >
            <img
              src={banner.cover}
              alt={banner.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
          </div>
        ))}

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center w-full px-4">
            <h1 className="text-3xl md:text-5xl font-heading text-white mb-3 drop-shadow-lg">
              羞羞歪歪漫画
            </h1>
            <p className="text-base md:text-lg text-white/90 mb-8 max-w-lg mx-auto drop-shadow-md">
              {siteInfo.slogan}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-read-line"></i>
                立即阅读
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white/20 backdrop-blur-sm text-white font-medium text-sm hover:bg-white/30 transition-all duration-300 cursor-pointer whitespace-nowrap border border-white/30"
              >
                了解更多
              </Link>
            </div>

            <div className="flex items-center justify-center gap-2 mt-8">
              {bannerComics.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentBanner(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                    idx === currentBanner
                      ? "bg-primary-500 w-8"
                      : "bg-white/50 hover:bg-white/80"
                  }`}
                  aria-label={`切换到第${idx + 1}张Banner`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-8 pb-6">
          {siteInfo.stats.map((stat, idx) => (
            <div key={idx} className="text-center">
              <div className="text-xl md:text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-white/70">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Category Navigation */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
            漫画分类
          </h2>
          <Link
            to="/category/all"
            className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-3">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer group"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                <i className={`${cat.icon} text-lg`}></i>
              </div>
              <span className="text-xs text-foreground-700 font-medium whitespace-nowrap">
                {cat.name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Hot Recommendations */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 rounded-full bg-accent-500" />
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                热门推荐
              </h2>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {hotComics.map((comic) => (
              <ComicCard key={comic.id} {...comic} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-1 h-5 rounded-full bg-primary-500" />
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
              最近更新
            </h2>
          </div>
          <Link
            to="/category/all"
            className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            更多 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
          {latestComics.map((comic) => (
            <ComicCard key={comic.id} {...comic} />
          ))}
        </div>
      </section>

      {/* Ranking */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-5 rounded-full bg-accent-500" />
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
              人气排行
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {rankComics.map((comic, idx) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="flex items-center gap-4 p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer group"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                  <span
                    className={`text-sm font-bold ${
                      idx < 3 ? "text-accent-600" : "text-foreground-500"
                    }`}
                  >
                    {idx + 1}
                  </span>
                </div>
                <div className="w-12 h-16 rounded overflow-hidden flex-shrink-0">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500">{comic.author}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-accent-600 flex items-center gap-0.5">
                      <i className="ri-star-fill text-[10px]"></i>
                      {comic.rating}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {(comic.views / 10000).toFixed(0)}万人气
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span
                    className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                      comic.status === "连载中"
                        ? "bg-primary-100 text-primary-700"
                        : "bg-secondary-100 text-secondary-700"
                    }`}
                  >
                    {comic.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20warm%20inviting%20reading%20atmosphere%20with%20soft%20golden%20light%2C%20abstract%20geometric%20patterns%20in%20warm%20coral%20and%20cream%20tones%2C%20cozy%20bookshelf%20silhouette%2C%20clean%20minimal%20aesthetic%2C%20horizontal%20wide%20composition&width=1600&height=400&seq=cta-bg-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-900/80 to-background-900/60" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-4">
          <h2 className="text-2xl md:text-4xl font-heading text-white mb-4">
            随时随地，畅享漫画世界
          </h2>
          <p className="text-white/80 mb-8 text-sm md:text-base max-w-xl mx-auto">
            羞羞歪歪漫画，始终致力于为读者提供最优质的漫画阅读体验。无论你在哪里，打开手机就能沉浸于精彩的漫画世界。
          </p>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-play-circle-line"></i>
            开始阅读
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center mb-8">
          常见问题
        </h2>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "羞羞歪歪漫画真的完全免费吗？",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "是的！羞羞歪歪漫画所有漫画资源完全免费阅读，无需付费订阅，无需会员充值。"
                  }
                },
                {
                  "@type": "Question",
                  "name": "如何在不同设备上阅读漫画？",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "羞羞歪歪漫画支持PC端和移动端访问。在手机上打开网站即可享受下拉式竖屏阅读体验。"
                  }
                },
                {
                  "@type": "Question",
                  "name": "漫画更新频率是怎样的？",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "我们与各大漫画平台保持同步更新，热门漫画基本每日更新。"
                  }
                },
                {
                  "@type": "Question",
                  "name": "漫画内容是否有删减？",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "羞羞歪歪漫画坚持提供原版完整内容，所有章节不做任何删减和修改。"
                  }
                },
                {
                  "@type": "Question",
                  "name": "如何找到想看的漫画？",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "您可以通过首页的分类导航浏览不同类型的漫画，也可以使用人气排行和热门推荐板块发现优质作品。"
                  }
                }
              ]
            })
          }}
        />
        <div className="space-y-3">
          {[
            {
              q: "羞羞歪歪漫画真的完全免费吗？",
              a: "是的！羞羞歪歪漫画所有漫画资源完全免费阅读，无需付费订阅，无需会员充值。我们致力于让每一位漫画爱好者都能无障碍地享受阅读的乐趣。",
            },
            {
              q: "如何在不同设备上阅读漫画？",
              a: "羞羞歪歪漫画支持PC端和移动端访问。在手机上打开网站即可享受下拉式竖屏阅读体验，完美适配各种屏幕尺寸。建议使用Chrome或Safari浏览器获得最佳体验。",
            },
            {
              q: "漫画更新频率是怎样的？",
              a: "我们与各大漫画平台保持同步更新，热门漫画基本每日更新。您可以在首页的「最近更新」板块查看最新上架的漫画章节。",
            },
            {
              q: "漫画内容是否有删减？",
              a: "羞羞歪歪漫画坚持提供原版完整内容，所有章节不做任何删减和修改，为读者呈现最真实的漫画故事。",
            },
            {
              q: "如何找到想看的漫画？",
              a: "您可以通过首页的分类导航浏览不同类型的漫画，也可以使用人气排行和热门推荐板块发现优质作品。我们提供了恋爱、热血、奇幻、搞笑、悬疑等多种分类供您选择。",
            },
          ].map((faq, idx) => (
            <details
              key={idx}
              className="group border border-background-200/70 rounded-lg overflow-hidden"
            >
              <summary className="flex items-center justify-between px-5 py-4 cursor-pointer bg-background-50 hover:bg-background-100 transition-colors">
                <span className="text-sm font-medium text-foreground-800">
                  {faq.q}
                </span>
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-5 pb-4">
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </p>
              </div>
            </details>
          ))}
        </div>
      </section>
    </main>
  );
}